import classNames from 'classnames'
import { AnimatePresence } from 'framer-motion'
import React, { MouseEventHandler, PropsWithChildren, forwardRef, useState } from 'react'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body2 } from '@ui/Typography/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { BaseFieldAnimation } from '@ui/components/Input/BaseField/BaseFieldAnimation'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { IconFCType } from '@ui/icons/types'

import styles from './BaseField.module.scss'

export type BaseFieldVariant =
  | 'default'
  | 'advanced-with-button'
  | 'digital'
  | 'textarea'
  | 'search'
export type InputSize = 'lg' | 'xl'
export type HintSize = 'sm' | 'md'
export type IconSize = 20 | 24
type IconHintSize = 16 | 18

interface CommonBaseFieldProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style'> {
  size?: InputSize
  variant?: BaseFieldVariant
  handleCopy?: () => void
  leftText?: string
  leftIcon?: IconFCType
  buttonComponent?: React.ReactNode
  labelSupportText?: string
  rightText?: string | { text: string; color: TextColorType }
  rightIcon?: IconFCType
  rightContent?: React.ReactNode
  renderRightIconJSX?: React.ReactNode
  hint?: string | React.ReactNode
  hintSupportText?: string
  hintIcon?: React.FC<any>
  disabled?: boolean
  error?: boolean
  readOnly?: boolean
  onWrapperClick?: MouseEventHandler<HTMLDivElement>
  onStartIconClick?: React.MouseEventHandler<HTMLSpanElement>
  onEndIconClick?: React.MouseEventHandler<HTMLSpanElement>
  fullWidth?: boolean
  isLabelActingAsPlaceholder?: boolean
  focused?: boolean
}

export type LabelConstraint =
  | {
      label: string
      placeholder?: string
    }
  | {
      label?: never
      placeholder?: never
    }

export type BaseFieldProps = CommonBaseFieldProps & LabelConstraint

const variantStyles: Record<BaseFieldVariant, string> = {
  default: '',
  'advanced-with-button': styles.variantAdvancedWithButton,
  digital: styles.variantDigital,
  textarea: styles.variantTextarea,
  search: styles.variantSearch,
} as const

const sizeStyles: Record<InputSize, string> = {
  lg: styles['size-lg'],
  xl: styles['size-xl'],
} as const

const hintSizeStyles: Record<HintSize, string> = {
  sm: styles.hintSizeSm,
  md: styles.hintSizeMd,
} as const

const getIconSize = (size?: InputSize): IconSize => {
  switch (size) {
    case 'lg':
      return 20
    case 'xl':
      return 24
    default:
      return 20
  }
}

const getHintSize = (size?: InputSize): HintSize => {
  switch (size) {
    case 'xl':
      return 'md'
    default:
      return 'sm'
  }
}

const getHintIconSize = (hintSize?: HintSize): IconHintSize => {
  switch (hintSize) {
    case 'sm':
      return 16
    case 'md':
      return 18
    default:
      return 16
  }
}

export const BaseField = forwardRef<HTMLDivElement, PropsWithChildren<BaseFieldProps>>(
  (props, ref) => {
    const {
      size = 'xl',
      variant = 'default',
      leftText,
      leftIcon: LeftIcon,
      buttonComponent,
      label,
      labelSupportText,
      handleCopy,
      rightText,
      rightIcon: RightIcon,
      rightContent,
      renderRightIconJSX,
      hint,
      hintSupportText,
      hintIcon: HintIcon,
      children,
      disabled = false,
      error = false,
      fullWidth,
      onWrapperClick,
      onStartIconClick,
      onEndIconClick,
      onFocusCapture,
      onBlurCapture,
      isLabelActingAsPlaceholder = false,
      focused,
      ...restProps
    } = props

    const [hasInternalFocus, setHasInternalFocus] = useState(false)

    const isFocused = focused ?? hasInternalFocus

    const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled && onWrapperClick) {
        onWrapperClick(event)
      }
    }

    const handleStartIconClick = (event: React.MouseEvent<HTMLSpanElement>) => {
      if (!disabled && onStartIconClick) {
        onStartIconClick(event)
      }
    }

    const handleEndIconClick = (event: React.MouseEvent<HTMLSpanElement>) => {
      if (!disabled && onEndIconClick) {
        onEndIconClick(event)
      }
    }

    const handleFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
      setHasInternalFocus(true)
      onFocusCapture?.(event)
    }

    const handleBlurCapture = (event: React.FocusEvent<HTMLDivElement>) => {
      setHasInternalFocus(false)
      onBlurCapture?.(event)
    }

    const iconSize = getIconSize(size)
    const hintSize = getHintSize(size)
    const hintIconSize = getHintIconSize(hintSize)
    const hasLeftGroup =
      leftText || LeftIcon || (variant === 'advanced-with-button' && buttonComponent)
    const hasLabel = label || labelSupportText
    const hasRightGroup = handleCopy || rightText || RightIcon || rightContent || renderRightIconJSX
    const hasHint = hint || hintSupportText || HintIcon
    const renderButtonComponent = variant === 'advanced-with-button' && buttonComponent

    return (
      <div
        ref={ref}
        className={classNames(styles.baseField, {
          [styles.stateFullWidth]: fullWidth,
          [styles.stateDisabled]: disabled,
          [styles.stateError]: error,
          [styles.stateFocused]: isFocused,
          [styles.stateClickable]: !disabled && !!onWrapperClick,
        })}
        onClick={handleContainerClick}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        {...restProps}
      >
        <div
          className={classNames(styles.fieldContainer, variantStyles[variant], sizeStyles[size], {
            [styles.noLabel]: !hasLabel,
          })}
        >
          {hasLeftGroup && (
            <div
              className={classNames(styles.leftGroup, {
                [styles.variantAdvancedWithButton]: variant === 'advanced-with-button',
                [styles.variantDefault]: variant === 'default',
              })}
              onClick={handleStartIconClick}
            >
              {renderButtonComponent ? (
                buttonComponent
              ) : (
                <>
                  {leftText && <span className={styles.leftLead}>{leftText}</span>}
                  {LeftIcon && (
                    <span className={styles.iconWrapper}>
                      <LeftIcon color='currentColor' size={iconSize} />
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          <div className={styles.contentArea}>
            {hasLabel && (
              <div
                className={classNames(styles.inputLabelWrapper, styles.labelSize, {
                  [styles.labelAsPlaceholder]: isLabelActingAsPlaceholder,
                })}
              >
                <Truncate>
                  {label && <span className={styles.inputLabel}>{label}</span>}
                  {labelSupportText && (
                    <span className={styles.labelSupportText}>({labelSupportText})</span>
                  )}
                </Truncate>
              </div>
            )}
            <div className={styles.inputContainer}>{children}</div>
          </div>

          {hasRightGroup && (
            <div className={styles.rightGroup} onClick={handleEndIconClick}>
              {rightText && (
                <span className={styles.trailText}>
                  {typeof rightText === 'object' ? (
                    <Body2 color={rightText.color} className={styles.trailText}>
                      {rightText.text}
                    </Body2>
                  ) : (
                    <Body2 className={styles.trailText} color='text-purple-high-em'>
                      {rightText}
                    </Body2>
                  )}
                </span>
              )}
              {renderRightIconJSX
                ? renderRightIconJSX
                : RightIcon && (
                    <span className={styles.iconWrapper}>
                      <RightIcon color='currentColor' size={iconSize} />
                    </span>
                  )}
              {handleCopy && (
                <span className={styles.iconWrapper}>
                  <ActionIcon
                    size={`x${iconSize}`}
                    icon={{ icon: CopyIcon, color: 'currentColor' }}
                    onClick={handleCopy}
                  />
                </span>
              )}
              {rightContent}
            </div>
          )}
        </div>

        <AnimatePresence initial={false}>
          {hasHint ? (
            <BaseFieldAnimation hasHint={!!hasHint}>
              <div className={styles.hintWrapper}>
                {HintIcon && (
                  <span className={styles.iconWrapper}>
                    <HintIcon color='currentColor' size={hintIconSize} />
                  </span>
                )}
                {hint && (
                  <Body2 className={classNames(styles.hint, hintSizeStyles[hintSize])}>
                    {hint}
                  </Body2>
                )}
                {hintSupportText && (
                  <span className={classNames(styles.hintSupportText, hintSizeStyles[hintSize])}>
                    ({hintSupportText})
                  </span>
                )}
              </div>
            </BaseFieldAnimation>
          ) : null}
        </AnimatePresence>
      </div>
    )
  }
)
